import { FormEvent, useState } from 'react';
import EvidenceFilesField from './EvidenceFilesField';
import { LiabilityFormErrors, LiabilityFormState } from './InsuranceLicenseTypes';

type Props = {
	form: LiabilityFormState;
	errors: LiabilityFormErrors;
	evidenceFiles: File[];
	evidenceError?: string;
	isSaving: boolean;
	onSubmit: (event: FormEvent<HTMLFormElement>) => void;
	onChange: (nextForm: LiabilityFormState) => void;
	onAddEvidenceFiles: (files: FileList | null) => void;
	onRemoveEvidenceFile: (index: number) => void;
	showAllErrors: boolean;
	isSubmitDisabled: boolean;
	submitLabel?: string;
	submittingLabel?: string;
};

export default function LiabilityInsuranceForm(props: Props) {
	const {
		form,
		errors,
		evidenceFiles,
		evidenceError,
		isSaving,
		onSubmit,
		onChange,
		onAddEvidenceFiles,
		onRemoveEvidenceFile,
		showAllErrors,
		isSubmitDisabled,
		submitLabel = 'Add new policy',
		submittingLabel = 'Adding...',
	} = props;

	const [touched, setTouched] = useState<Partial<Record<keyof LiabilityFormState, boolean>>>({});

	const shouldShowError = (field: keyof LiabilityFormState) =>
		Boolean((showAllErrors || touched[field]) && errors[field]);

	const markTouched = (field: keyof LiabilityFormState) => () => setTouched((prev) => ({ ...prev, [field]: true }));

	return (
		<form onSubmit={onSubmit}>
			<fieldset className="insurance-license-form-fieldset" disabled={isSaving}>
				<div className="insurance-license-input-row">
					<div className="insurance-license-input-item">
						<label htmlFor="MarketClientInsuranceName">Insurance provider</label>
						<input
							id="MarketClientInsuranceName"
							type="text"
							className="form-control"
							value={form.insuranceProvider}
							onChange={(event) => onChange({ ...form, insuranceProvider: event.target.value })}
							onBlur={markTouched('insuranceProvider')}
							aria-invalid={shouldShowError('insuranceProvider')}
						/>
						{shouldShowError('insuranceProvider') ? (
							<div className="insurance-license-field-error">{errors.insuranceProvider}</div>
						) : null}
					</div>
					<div className="insurance-license-input-item">
						<label htmlFor="MarketClientInsurancePolicyNumber">Policy number</label>
						<input
							id="MarketClientInsurancePolicyNumber"
							type="text"
							className="form-control"
							value={form.policyNumber}
							onChange={(event) => onChange({ ...form, policyNumber: event.target.value })}
							onBlur={markTouched('policyNumber')}
							aria-invalid={shouldShowError('policyNumber')}
						/>
						{shouldShowError('policyNumber') ? (
							<div className="insurance-license-field-error">{errors.policyNumber}</div>
						) : null}
					</div>
					<div className="insurance-license-input-item">
						<label htmlFor="MarketClientInsuranceValidFrom">Valid from date</label>
						<input
							id="MarketClientInsuranceValidFrom"
							type="date"
							className="form-control"
							value={form.validFrom}
							onChange={(event) => onChange({ ...form, validFrom: event.target.value })}
							onBlur={markTouched('validFrom')}
							aria-invalid={shouldShowError('validFrom')}
						/>
						{shouldShowError('validFrom') ? (
							<div className="insurance-license-field-error">{errors.validFrom}</div>
						) : null}
					</div>
					<div className="insurance-license-input-item">
						<label htmlFor="MarketClientInsuranceExpiry">Expiry date</label>
						<input
							id="MarketClientInsuranceExpiry"
							type="date"
							className="form-control"
							value={form.expiryDate}
							onChange={(event) => onChange({ ...form, expiryDate: event.target.value })}
							onBlur={markTouched('expiryDate')}
							aria-invalid={shouldShowError('expiryDate')}
						/>
						{shouldShowError('expiryDate') ? (
							<div className="insurance-license-field-error">{errors.expiryDate}</div>
						) : null}
					</div>
				</div>

				<EvidenceFilesField
					inputId="LiabilityInsuranceEvidenceFiles"
					files={evidenceFiles}
					error={evidenceError}
					showError={showAllErrors}
					disabled={isSaving}
					onAddFiles={onAddEvidenceFiles}
					onRemoveFile={onRemoveEvidenceFile}
				/>

				<div className="insurance-license-form-actions">
					<button type="submit" className="btn btn-sm btn-default" disabled={isSubmitDisabled || isSaving}>
						{isSaving ? submittingLabel : submitLabel}
					</button>
				</div>

				<div className="insurance-license-sticky-actions">
					<button type="submit" className="btn btn-primary" disabled={isSubmitDisabled || isSaving}>
						{isSaving ? submittingLabel : submitLabel}
					</button>
				</div>
			</fieldset>
		</form>
	);
}
